import { InvalidFileInfo } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/types.ts';
import {
  createAlertDetailSingleMessage,
  createAlertMultipleMessage,
  validateImages,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductThumbnailForm/helper.ts';
import {
  FileUploadError,
  FileUploadErrorJson,
  FileUploadHelper,
} from '@services/MarppleShop/shared/components/cells/FileUpload/FileUploadHelper.ts';
import { EXTENSION_TO_MIME_TYPE } from '@services/MarppleShop/shared/components/cells/FileUpload/FileExtensions.ts';
import {
  PRODUCT_STORY_MAX_HEIGHT,
  PRODUCT_STORY_MAX_WIDTH,
} from '@modules/Creator/ProductStoryEditor/S/width';

/**
 * 이미지 처리를 담당하는 유틸리티 클래스
 * 레거시 코드의 이미지 처리 로직을 이식한 버전
 */
export class ImageProcessors {
  // 상수 정의 (레거시 코드에서 가져옴)
  static readonly MAX_FILE_SIZE = 5; // 5MB
  static readonly MAX_FILES = 10;
  static readonly PRODUCT_STORY_MAX_WIDTH = PRODUCT_STORY_MAX_WIDTH;
  static readonly PRODUCT_STORY_MAX_HEIGHT = PRODUCT_STORY_MAX_HEIGHT;

  /**
   * 파일 개수를 검증합니다.
   */
  static validateFileCount(
    currentCount: number,
    newCount: number,
  ): { valid: boolean; error?: FileUploadErrorJson } {
    const totalCount = currentCount + newCount;
    if (totalCount > this.MAX_FILES) {
      return {
        valid: false,
        error: {
          title: '이미지 개수 초과',
          message: `업로드 가능한 개수를 초과하였습니다.\n${this.MAX_FILES}장 이하로 업로드해 주세요.`,
        },
      };
    }
    return { valid: true };
  }

  /**
   * 이미지를 리사이즈합니다.
   * 레거시 코드의 이미지 리사이즈 로직을 이식
   */
  static async resizeImage(file: File): Promise<{
    dataUrl: string;
    width: number;
    height: number;
    file_id: string;
  }> {
    if (EXTENSION_TO_MIME_TYPE?.gif?.includes(file.type)) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const image = new Image();
          image.onload = () => {
            const maxSize = this.PRODUCT_STORY_MAX_WIDTH;

            // 이미지가 최대 크기를 초과하는 경우 리사이즈
            if (image.width > maxSize) {
              image.width = maxSize;
            }

            const fileId = this.generateUniqueId();

            resolve({
              dataUrl: reader.result as string,
              width: image.width,
              height: image.height,
              file_id: fileId,
            });
          };
          image.onerror = () => {
            reject(new Error('Failed to load image'));
          };
          image.src = reader.result as string;
        };
        reader.onerror = () => {
          reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
      });
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => {
          const maxSize = this.PRODUCT_STORY_MAX_WIDTH;

          // 이미지가 최대 크기를 초과하는 경우 리사이즈
          if (image.width > maxSize) {
            image.width = maxSize;
          }

          // Canvas를 사용하여 이미지를 리사이즈하고 데이터 URL을 생성
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          canvas.width = image.width;
          canvas.height = image.height;

          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL(file.type);
          const fileId = this.generateUniqueId();

          resolve({
            dataUrl,
            width: canvas.width,
            height: canvas.height,
            file_id: fileId,
          });
        };
        image.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        image.src = reader.result as string;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 파일에 고유 ID를 생성합니다.
   * 레거시 코드의 makeUniqId 함수를 이식
   */
  private static fileIdCounter = 1;
  static generateUniqueId(): string {
    return 'file_' + this.fileIdCounter++;
  }

  /**
   * 이미지 파일을 처리하여 표시 가능한 형태로 변환합니다.
   * 레거시 코드의 setProductStoryImage 로직을 이식
   */
  static async processImageFile(file: File): Promise<{
    id: number;
    file_id: string;
    url: string;
    width: number;
    height: number;
    originalFile: File & { file_id: string };
  }> {
    // 이미지 리사이즈 및 처리
    const processedImage = await this.resizeImage(file);

    // file 객체에 file_id 추가 (레거시 코드 호환성) - 타입 안전하게
    const fileWithId = Object.assign(file, { file_id: processedImage.file_id });
    return {
      id: 0, // 새 이미지는 id가 0 (레거시 코드 로직)
      file_id: processedImage.file_id,
      url: processedImage.dataUrl,
      width: processedImage.width,
      height: processedImage.height,
      originalFile: fileWithId,
    };
  }

  /**
   * 여러 이미지 파일을 일괄 처리합니다.
   */
  static async processMultipleImageFiles(
    files: FileList | File[],
    currentImageCount = 0,
  ): Promise<{
    success: boolean;
    processedImages?: Array<{
      id: number;
      file_id: string;
      url: string;
      width: number;
      height: number;
      originalFile: File & { file_id: string };
    }>;
    error?: FileUploadErrorJson;
  }> {
    const fileArray = Array.from(files);

    // 파일 개수 검증
    const countValidation = this.validateFileCount(currentImageCount, fileArray.length);
    if (!countValidation.valid) {
      return { success: false, error: countValidation.error };
    }

    const validator = FileUploadHelper.createHelper({
      allowed_extensions: ['jpg', 'jpeg', 'png', 'gif'],
      allowed_mime_types: EXTENSION_TO_MIME_TYPE.img_wild,
      max_width: this.PRODUCT_STORY_MAX_WIDTH,
      max_height: this.PRODUCT_STORY_MAX_HEIGHT,
      min_width: 0,
      min_height: 0,
      max_file_size: this.MAX_FILE_SIZE,
    });
    const invalidFiles: InvalidFileInfo[] = [];
    const validFiles = await validateImages(fileArray, invalidFiles, validator);

    if (invalidFiles.length > 0) {
      const alertOptions =
        fileArray.length > 1
          ? createAlertMultipleMessage(invalidFiles, validator)
          : createAlertDetailSingleMessage(invalidFiles, validator);
      throw new FileUploadError(alertOptions);
    }

    // 모든 이미지 파일을 병렬로 처리
    const processedImages = await Promise.all(validFiles.map((file) => this.processImageFile(file)));

    return {
      success: true,
      processedImages,
    };
  }
}
